/**
 * Waitlist Form (E20-1123)
 *
 * Client-side controller for the back-in-stock "Join the Waitlist" modal
 * (bfx-waitlist-modal.liquid) and the Lead Capture section
 * (bfx-lead-capture.liquid, E20-1184). Validates the email on submit and
 * surfaces a validation message on an empty/malformed address.
 *
 * Two success treatments, chosen by `data-success`:
 *   - default (modal): swaps the body for the success confirmation panel.
 *   - `inline` (section): clears + locks the field, shows the confirmation where
 *     the placeholder was, and flips it to the success colour (via `is-success`).
 *
 * Inline mode pairs its error state with the same field-colour-flip pattern
 * (via `is-error`), just in red instead of green and without clearing/locking
 * the field, so a shopper can retry immediately.
 *
 * On a valid submit it dispatches a bubbling `lead_capture:submit` event whose
 * detail carries { email, sku, market, source } — the captured lead that the
 * Braze send (E20-1185 / E20-1094, assets/bfx-braze-lead-capture.js) consumes.
 * The submit button is disabled while that send is in flight; the form then
 * waits for the sender's `lead_capture:result` event ({ success }) before
 * showing success or error, rather than assuming success on submit (E20-1472).
 */
// The form unlocks only when `lead_capture:result` arrives, and the sole dispatcher of that
// event is bfx-braze-lead-capture.js — a type="module" resolving 'util.braze' through the import
// map. If that module never evaluates (Safari below 16.4 has no import-map support, or the asset
// fetch fails), the listener never registers and the form would sit disabled with a read-only
// field and nothing on screen explaining why. This guard fails it closed instead, so the shopper
// gets the submit error and can retry rather than a dead form. 10s is well clear of the sender's
// own ceiling — RETRY_MS 200 x RETRY_MAX 10 is ~2s in util.braze.js — so it can only fire when
// nothing is coming at all, never ahead of a slow-but-successful send.
const RESULT_TIMEOUT_MS = 10000;

class WaitlistForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-waitlist-form]');
    this.emailInput = this.querySelector('[data-waitlist-email]');
    this.errorEl = this.querySelector('[data-waitlist-error]');
    this.successEl = this.querySelector('[data-waitlist-success]');
    this.submitBtn = this.querySelector('button[type="submit"]');
    this.inlineSuccess = this.dataset.success === 'inline';
    this.body = this.querySelector('[data-waitlist-body]'); // modal body-swap layout only

    if (!this.form || !this.emailInput || !this.errorEl || !this.successEl) return;
    if (!this.inlineSuccess && !this.body) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.form.addEventListener('submit', (e) => this.onSubmit(e), { signal });
    this.emailInput.addEventListener('input', () => this.clearError(), { signal });
    this.addEventListener('lead_capture:result', (e) => this.onResult(e.detail.success), {
      signal,
    });

    // Modal only: keep the SKU fresh as the shopper switches variants, so the
    // lead_capture:submit payload never carries a stale SKU (theme.js fires this).
    // The Lead Capture section (inline mode) uses a fixed, section-configured SKU,
    // so it must ignore variant:change or an unrelated variant swap would clobber it.
    if (!this.inlineSuccess) {
      document.addEventListener(
        'variant:change',
        (e) => {
          const variant = e.detail?.variant;
          if (variant?.sku) this.dataset.sku = variant.sku;
        },
        { signal }
      );
    }
  }

  onSubmit(event) {
    event.preventDefault();
    // Lowercased so the Braze send (assets/bfx-braze-lead-capture.js) resolves case
    // variants of the same address to one external_id instead of two profiles.
    const email = this.emailInput.value.trim().toLowerCase();

    if (!this.isValidEmail(email)) {
      this.showError();
      return;
    }

    // Expose the captured lead for the Braze send (assets/bfx-braze-lead-capture.js),
    // which reports back via lead_capture:result once it knows the outcome. Clears
    // any error left over from a prior failed attempt — without this, retrying the
    // same valid email (no `input` event to trigger clearError()) would leave that
    // stale alert and is-error styling visible for the whole new attempt.
    this.clearError();
    this.setSubmitting(true);
    this.armResultTimeout();
    this.dispatchEvent(
      new CustomEvent('lead_capture:submit', {
        bubbles: true,
        detail: {
          email,
          sku: this.dataset.sku || '',
          market: this.dataset.market || '',
          source: this.dataset.source || '',
        },
      })
    );
  }

  // Fails the send closed if no result arrives. Separate method so the timer is always
  // paired with a clearResultTimeout() and never left running against a detached element.
  armResultTimeout() {
    this.clearResultTimeout();
    this.resultTimeoutId = window.setTimeout(() => {
      // Say why in the console, not just on screen. This is the one failure path where
      // nothing else reports a cause: util.braze.js logs both of its outcomes, but if the
      // sender module never evaluated then none of that code ran, so without this the
      // shopper's "Something went wrong" would have no counterpart anywhere for QA to read.
      console.warn(
        `bfx-waitlist-form: no lead_capture:result within ${RESULT_TIMEOUT_MS}ms — the Braze ` +
          'sender (bfx-braze-lead-capture.js) never reported back, so the form was released ' +
          'with an error rather than left disabled. Most likely that module failed to ' +
          'evaluate: no import-map support (Safari below 16.4) or a failed asset fetch.'
      );
      this.onResult(false);
    }, RESULT_TIMEOUT_MS);
  }

  clearResultTimeout() {
    if (this.resultTimeoutId) {
      window.clearTimeout(this.resultTimeoutId);
      this.resultTimeoutId = null;
    }
  }

  // Handles the Braze send's outcome (assets/bfx-braze-lead-capture.js), or the timeout
  // guard above when that outcome never arrives.
  onResult(success) {
    this.clearResultTimeout();
    this.setSubmitting(false);
    if (success) {
      this.showSuccess();
    } else {
      // Not an invalid-email error — don't mark the (valid) email as invalid or steal focus.
      this.showError(this.errorEl.dataset.submitErrorMessage, { markInputInvalid: false });
    }
  }

  // Disables the submit button while a Braze send is in flight, so a double-click
  // can't dispatch a second lead_capture:submit and inflate the Braze event count.
  // Also locks the email field itself — the send can take ~2s (the readiness
  // poll), and without this a shopper editing the address mid-flight would see
  // showSuccess() clear/lock whatever they'd typed by then, not the value that
  // was actually dispatched. onResult() unlocks it again before branching, so
  // showSuccess() re-locks it on success but a failure leaves it editable.
  setSubmitting(isSubmitting) {
    if (this.submitBtn) this.submitBtn.disabled = isSubmitting;
    this.emailInput.readOnly = isSubmitting;
  }

  // Require text either side of a single @ and a dotted domain — mirrors the
  // native type=email check while rejecting the addresses it lets through
  // (e.g. "a@b"). Kept deliberately loose; the authoritative check is server-side.
  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  showError(message = this.errorEl.dataset.message, { markInputInvalid = true } = {}) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    if (markInputInvalid) {
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.focus();
    }
    // Pairs visually with showSuccess()'s field-colour flip (inline treatment only).
    if (this.inlineSuccess) this.form.classList.add('is-error');
  }

  clearError() {
    if (this.errorEl.hidden) return;
    this.errorEl.hidden = true;
    this.emailInput.removeAttribute('aria-invalid');
    if (this.inlineSuccess) this.form.classList.remove('is-error');
  }

  showSuccess() {
    if (this.inlineSuccess) {
      // Inline treatment (Lead Capture section): clear + lock the field, show the
      // confirmation where the placeholder was, and flip it to the success colour.
      const message = this.dataset.successMessage || '';
      this.emailInput.value = '';
      this.emailInput.readOnly = true;
      this.emailInput.setAttribute('placeholder', message);
      this.form.classList.add('is-success');
      if (this.submitBtn) this.submitBtn.disabled = true;
      this.clearError();
      // The placeholder swap isn't announced; mirror it into the live region.
      this.successEl.textContent = message;
      this.successEl.hidden = false;
      return;
    }

    // Body-swap treatment (PDP waitlist modal).
    this.body.hidden = true;
    this.successEl.hidden = false;
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.clearResultTimeout();
  }
}

if (!customElements.get('waitlist-form')) {
  customElements.define('waitlist-form', WaitlistForm);
}
